import React, { useEffect, useRef, useState } from 'react';
import { AtSign } from 'lucide-react';

/**
 * MentionAutocomplete — a textarea that surfaces an @user dropdown
 * inline as the user types '@'. Hits /api/users/mentionable for matches.
 *
 * Props:
 *   value, onChange:    standard controlled textarea
 *   api, projectId:     for the autocomplete query
 *   placeholder, rows, dataTestid, disabled, onEnterSubmit (Cmd+Enter)
 */
export default function MentionAutocomplete({
  value, onChange, api, projectId,
  placeholder = 'Type a message… use @name to tag someone',
  rows = 2, dataTestid, disabled = false, onEnterSubmit,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const [anchorIdx, setAnchorIdx] = useState(-1);   // pos of '@' that opened the dropdown
  const taRef = useRef(null);

  // Detect @-trigger and current query
  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    const caret = e.target.selectionStart;
    const before = v.slice(0, caret);
    // Find the last '@' not preceded by a word char (so emails don't trigger)
    const m = /(^|[\s\n])@([A-Za-z0-9._-]{0,40})$/.exec(before);
    if (m) {
      const at = caret - (m[2].length + 1);   // position of '@'
      setOpen(true); setAnchorIdx(at); setQuery(m[2]); setHighlight(0);
    } else {
      setOpen(false); setAnchorIdx(-1); setQuery('');
    }
  };

  // Live query
  useEffect(() => {
    if (!open) return;
    const ctrl = new AbortController();
    api.get('/users/mentionable', {
      params: { q: query, project_id: projectId, limit: 8 },
      signal: ctrl.signal,
    }).then(r => setHits(r.data?.items || [])).catch(() => {});
    return () => ctrl.abort();
  }, [open, query, projectId]);

  const insertHandle = (user) => {
    if (anchorIdx < 0) return;
    const ta = taRef.current;
    // Prefer the canonical handle the backend ships; fall back to a derived
    // slug from full_name. NEVER fall back to email (clients don't even
    // receive emails in the mention payload anymore).
    const handle = (user.handle || (user.full_name || '').toLowerCase().split(/\s+/)[0].replace(/[^a-z0-9]/g, '') || 'user');
    const before = value.slice(0, anchorIdx);
    const after = value.slice(anchorIdx + 1 + query.length);
    const next = `${before}@${handle} ${after}`;
    onChange(next);
    setOpen(false); setHits([]); setHighlight(0);
    // Place caret right after the inserted handle
    requestAnimationFrame(() => {
      if (ta) {
        const newCaret = (before.length + 1 + handle.length + 1);
        ta.focus();
        ta.setSelectionRange(newCaret, newCaret);
      }
    });
  };

  const onKeyDown = (e) => {
    if (open && hits.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => (h + 1) % hits.length); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => (h - 1 + hits.length) % hits.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertHandle(hits[highlight]); return; }
      if (e.key === 'Escape')    { setOpen(false); return; }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && onEnterSubmit) {
      e.preventDefault();
      onEnterSubmit();
    }
  };

  return (
    <div className="relative w-full" data-testid="mention-autocomplete">
      <textarea ref={taRef} value={value} onChange={handleChange} onKeyDown={onKeyDown}
        placeholder={placeholder} rows={rows} disabled={disabled}
        data-testid={dataTestid}
        className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500/50 resize-none" />
      {open && hits.length > 0 && (
        <div className="absolute bottom-full left-0 mb-1 z-30 bg-zinc-900 border border-zinc-700 rounded-md shadow-lg w-72 max-h-60 overflow-y-auto"
             data-testid="mention-suggest-list">
          {hits.map((u, i) => {
            const active = i === highlight;
            return (
              <button key={u.id} type="button"
                onMouseDown={(e) => { e.preventDefault(); insertHandle(u); }}
                onMouseEnter={() => setHighlight(i)}
                data-testid={`mention-suggest-${u.id}`}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 ${active ? 'bg-violet-500/15' : ''}`}>
                <AtSign className="w-3 h-3 text-violet-300 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className={`text-xs ${active ? 'text-violet-200' : 'text-zinc-100'} truncate flex items-center gap-1.5`}>
                    <span className="font-medium">{u.full_name}</span>
                    {u.handle ? <span className="text-cyan-400 font-mono text-[10px]">@{u.handle}</span> : null}
                  </div>
                  <div className="text-[10px] text-zinc-500 truncate">
                    {u.role?.replace(/_/g, ' ')}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
